/******************************************************************************
 * Memokid: A game to train one's memory                                      *
 * Copyright (C) 2022 Lukas Laag                                              *
 * This file is part of Memokid                                               *
 *                                                                            *
 * Memokid is free software: you can redistribute it and/or modify            *
 * it under the terms of the GNU General Public License as published by       *
 * the Free Software Foundation, either version 3 of the License, or          *
 * (at your option) any later version.                                        *
 *                                                                            *
 * Memokid is distributed in the hope that it will be useful,                 *
 * but WITHOUT ANY WARRANTY; without even the implied warranty of             *
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the              *
 * GNU General Public License for more details.                               *
 *                                                                            *
 * You should have received a copy of the GNU General Public License          *
 * along with Memokid.  If not, see <https://www.gnu.org/licenses/>.          *
 *                                                                            *
 ******************************************************************************/

import { useCallback, useState } from 'react'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import HomeIcon from '@mui/icons-material/Home'
import GameArea from '../components/GameArea'
import { PlayerScores } from './PlayerScores'
import { ensureLoaded, flipCard, demoLeave } from '../actions/game'
import { go, routes } from '../location'
import './GameScreen.css'
import { LEVELS, log } from '../logs'

const getWinners = (playerCards, players) => {
  const scores = playerCards.map((obj) => obj.cardRefids.length)
  // Draw if all player scores are the same, see http://stackoverflow.com/questions/14832603/check-if-all-values-of-array-are-equal
  if (scores.length && !!scores.reduce((a, b) => (a === b) ? a : NaN)) {
    return null
  }
  const max = Math.max(...scores)
  return playerCards
    .filter((obj) => obj.cardRefids.length === max)
    .map((obj) => players.find((player) => player.id === obj.refid).name)
}

const GameScreen = props => {
  log('GameScreen', LEVELS.INFO, props)
  const {
    decks,
    game,
    players,
    settings: { deckRefid },
    dispatch
  } = props
  const intl = useIntl()
  const { fieldCards, playerCards } = game
  const deck = ensureLoaded(decks[deckRefid], dispatch)
  const cards = deck.cards
  const winners = getWinners(playerCards, players)
  const nameList = (names) => {
    if (!names) {
      return ''
    }
    if (names.length === 1) {
      return names[0]
    }
    return `${names.slice(0, names.length - 1).join(' ,')} ${intl.formatMessage({ id: 'game.conjunction' })} ${names[names.length - 1]}`
  }

  const [dialog, setDialog] = useState(null)

  const showDialog = useCallback(() => {
    setDialog('confirm')
  }, [])

  const hideDialog = useCallback(() => {
    setDialog(null)
  }, [])

  const stopGame = useCallback(() => {
    setDialog(null)
    dispatch(go(routes.HOME))
  }, [dispatch])

  const handleGameEnd = useCallback(() => dispatch(go(routes.SCORES)), [dispatch])

  return (
    <div
      className='game-screen'
      onMouseMove={() => dispatch(demoLeave())}
    >
      <GameArea
        cardClick={(index) => dispatch(flipCard(index))}
        cards={cards}
        fieldCards={fieldCards}
      />
      <div className='game-info'>
        <PlayerScores cards={cards} players={players} game={game} />
        <Button
          variant='outlined'
          startIcon={<HomeIcon />}
          onClick={showDialog}
        >{intl.formatMessage({ id: 'common.home' })}
        </Button>
      </div>
      <Dialog
        open={dialog === 'confirm'}
      >
        <DialogContent><DialogContentText>{intl.formatMessage({ id: 'game.stopMessage' })}</DialogContentText></DialogContent>
        <DialogActions>
          <Button autoFocus onClick={hideDialog}>{intl.formatMessage({ id: 'common.cancel' })}</Button>
          <Button onClick={stopGame}>{intl.formatMessage({ id: 'common.home' })}</Button>
        </DialogActions>
      </Dialog>
      {
        fieldCards.length === 0
          ? <Dialog
              open
            >
            <DialogTitle>{intl.formatMessage({ id: 'game.gameOver' })}</DialogTitle>
            <DialogContent><DialogContentText>{intl.formatMessage({ id: winners ? 'game.win' : 'game.draw' }, { players: nameList(winners), count: winners ? winners.length : 0 })}</DialogContentText></DialogContent>
            <DialogActions>
              <Button autoFocus onClick={handleGameEnd}>{intl.formatMessage({ id: 'common.ok' })}</Button>
            </DialogActions>
            </Dialog>
          : null
      }
    </div>
  )
}
GameScreen.displayName = 'GameScreen'

const mapStateToProps = ({ decks, game, players, settings }) => ({ decks, game, players, settings })

export default connect(mapStateToProps)(GameScreen)
